import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Boxes, RefreshCw, CheckCircle2, Lock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import CageBalancingTabs from '../components/CageBalancingTabs';
import PinModal from '../components/CagePinModal';
import CageDatePicker from '../components/CageDatePicker';
import { useCageDate } from '../contexts/CageDateContext';
import { useAuth } from '../contexts/AuthContext';
import { enterMovesDown } from '../utils/cbsGridNav';

// ----------------------------------------------------------------
// Cage Drop Count
// ----------------------------------------------------------------
// Physical chip count of each table's drop box (kind='drop') and jackpot drop
// (kind='jackpot'), by denomination. Rolls up to Drop/Jackpot totals by game +
// grand total. On commit, per-table drop + jackpot post to cbs_box_counts (table
// revenue + JE) and the grand-total drop fills the shift's Box Count component.

const fmtCents = (c) =>
  (Number(c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const denomLabel = (d) => `$${(Number(d) / 100).toLocaleString('en-US')}`;
export default function CageDropCount() {
  const { can } = useAuth();
  const canOperate = can('cbs:operate');
  const { businessDate: date, setBusinessDate: setDate } = useCageDate();
  const [shift, setShift] = useState('day');
  const [layout, setLayout] = useState(null);
  const [counts, setCounts] = useState({}); // `${table}:${kind}:${denom}` -> string
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [lastPost, setLastPost] = useState(null);
  const [pin, setPin] = useState(null);
  const [chipColors, setChipColors] = useState({});
  const [editColors, setEditColors] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [shiftList, setShiftList] = useState([]);

  const key = (t, k, d) => `${t}:${k}:${d}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [layRes, cntRes, shRes, colRes] = await Promise.all([
        api.get('/admin/cbs/drop-counts/layout'),
        api.get('/admin/cbs/drop-counts', { params: { date, shift } }),
        api.get('/admin/cbs/shifts', { params: { date } }),
        api.get('/admin/cbs/chip-colors'),
      ]);
      setLayout(layRes.data);
      const m = {};
      for (const r of cntRes.data.counts || []) m[key(r.tableNumber, r.kind, Number(r.denomCents))] = r.count;
      setCounts(m);
      const sh = (shRes.data.shifts || []).find((x) => x.shift === shift);
      setLocked(!!(sh && sh.status === 'closed'));
      setChipColors((colRes && colRes.data && colRes.data.colors) || {});
      setShiftList((shRes && shRes.data && shRes.data.shifts) || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load drop count.');
    } finally {
      setLoading(false);
    }
  }, [date, shift]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => { setLastPost(null); }, [date, shift]);

  // The drop is counted once a day. On 09/14 it went onto the day shift and then
  // onto the swing as well, and nothing said so - over/short cannot catch it,
  // because box count enters the formula twice with opposite signs and cancels.
  // It surfaced only as a doubled journal and doubled jackpot meters. So: say it.
  const thisShift = shiftList.find((x) => x.shift === shift) || null;
  const otherCommitted = shiftList.filter(
    (x) => x.shift !== shift && Number(x.boxCountCents || 0) > 0
  );
  const thisCommittedCents = Number((thisShift && thisShift.boxCountCents) || 0);

  const raw = (t, k, d) => counts[key(t, k, d)];
  const num = (t, k, d) => Number(raw(t, k, d) || 0);
  const colorFor = (d) => chipColors[d] || '#6b7280';
  const saveColors = async () => {
    setSavingColors(true);
    try {
      const res = await api.put('/admin/cbs/chip-colors', { colors: chipColors });
      setChipColors((res.data && res.data.colors) || chipColors);
      toast.success('Chip colors saved');
      setEditColors(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save colors');
    } finally {
      setSavingColors(false);
    }
  };
  const setCount = (t, k, d, v) => setCounts((p) => ({ ...p, [key(t, k, d)]: v }));
  const save = async (t, k, d, v) => {
    if (!canOperate) return;
    try {
      await api.post('/admin/cbs/drop-counts', {
        businessDate: date, shift, tableNumber: t, kind: k, denomCents: d, count: Number(v) || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save count.');
    }
  };

  const boxTotal = (t, k, denoms) => denoms.reduce((a, d) => a + d * num(t, k, d), 0);

  const buildPayload = () => {
    const payload = [];
    for (const [k, v] of Object.entries(counts)) {
      const cnt = Number(v) || 0;
      if (cnt <= 0) continue;
      const [t, kind, d] = k.split(':');
      payload.push({ tableNumber: Number(t), kind, denomCents: Number(d), count: cnt });
    }
    return payload;
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.post('/admin/cbs/drop-counts/bulk', { businessDate: date, shift, counts: buildPayload() });
      toast.success('Drop count saved.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  // Game rollups
  const dropByGame = {};
  const jackpotByGame = {};
  let grandDrop = 0;
  let grandJackpot = 0;
  if (layout) {
    for (const b of layout.dropBoxes) {
      const v = boxTotal(b.tableNumber, 'drop', layout.dropDenoms);
      dropByGame[b.gameLabel] = (dropByGame[b.gameLabel] || 0) + v;
      grandDrop += v;
    }
    for (const b of layout.jackpotBoxes) {
      const v = boxTotal(b.tableNumber, 'jackpot', layout.jackpotDenoms);
      jackpotByGame[b.gameLabel] = (jackpotByGame[b.gameLabel] || 0) + v;
      grandJackpot += v;
    }
  }

  // Two paths, one button. An OPEN shift takes the ordinary commit. A CLOSED
  // one goes through post-drop, which writes the box count without unlocking the
  // sheet, recomputes it in place, and carries the corrected ending into the next
  // shift. Reopening a settled shift to enter the morning drop is what put
  // 2026-09-28 out by $3,630, so it is no longer the way in.
  // Posting to a closed sheet. Dual control, same as closing or reopening one —
  // it moves a settled shift and the opening balance of the shift after it.
  // `cascadeClosed` is sent only after the backend has refused once and named
  // exactly which settled sheets would change; it is never sent speculatively.
  const postDrop = async ({ employeeNumber, staffPin }, cascadeClosed = false) => {
    const out = await api.post('/admin/cbs/post-drop', {
      businessDate: date, shift, counts: buildPayload(),
      employeeNumber, staffPin, cascadeClosed,
    });
    const d = out.data || {};
    setLastPost(d);
    toast.success(`Posted ${fmtCents(d.boxCountCents)} to the closed ${shift} shift — it was not reopened.`);
    for (const step of d.propagated || []) {
      if (step.deltaCents) {
        toast.success(
          `${step.label} opening ${step.deltaCents > 0 ? 'up' : 'down'} ${fmtCents(Math.abs(step.deltaCents))}` +
          (step.wasClosed ? ' (settled sheet corrected)' : ''),
          { duration: 7000 }
        );
      } else if ((step.skippedOverrides || []).length) {
        toast(
          `${step.label} was left alone — its ${step.skippedOverrides.join(' and ')} opening was entered by hand.`,
          { icon: '✋', duration: 9000 }
        );
      }
    }
    for (const w of d.warnings || []) toast(w, { icon: '⚠️', duration: 14000 });
    await load();
  };

  const commit = async () => {
    if (locked) {
      // The backend refuses, before writing anything, if a settled sheet would
      // move. That refusal is the prompt: re-confirm, and it cascades.
      setPin({
        title: 'Post the drop',
        subtitle: `Supervisor PIN. Posts ${fmtCents(grandDrop + grandJackpot)} to the closed ${shift} shift for ${date} without reopening it.`,
        submitLabel: 'Post the drop',
        onSubmit: async (creds) => {
          try {
            await postDrop(creds, false);
          } catch (e) {
            const msg = e?.response?.data?.error || '';
            if (/already closed/i.test(msg) && /cascadeClosed/i.test(msg)) {
              if (!window.confirm(`${msg}\n\nCorrect the settled sheet(s) as well?`)) {
                toast('Nothing was posted.', { icon: '↩️' });
                return;
              }
              await postDrop(creds, true);
              return;
            }
            throw e;
          }
        },
      });
      return;
    }
    setCommitting(true);
    try {
      const out = await api.post('/admin/cbs/drop-counts/commit', { businessDate: date, shift, counts: buildPayload() });
      const d = out.data || {};
      if (d.shiftUpdated) toast.success(`Committed to ${shift} shift — box count ${fmtCents(d.boxCountCents)}.`);
      else toast(`Counts saved, but no ${shift} shift exists for ${date} to post the box count to.`, { icon: '⚠️' });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to commit.');
    } finally {
      setCommitting(false);
    }
  };

  // Render helper (NOT a component) so inputs stay mounted while typing.
  const renderBox = (b, kind, denoms) => (
    <div key={`${kind}${b.tableNumber}`} onKeyDown={enterMovesDown} className="rounded-lg border border-gray-700 bg-gray-900 p-3">
      <div className="mb-2 text-sm font-semibold text-gray-200">
        {b.gameLabel} <span className="text-gray-500">#{b.tableNumber}</span>
      </div>
      <div className="space-y-1">
        {denoms.map((d) => (
          <div key={d} className="flex items-center justify-between gap-2 border-l-2 pl-2" style={{ borderColor: colorFor(d) }}>
            <span className="flex items-center gap-1.5 text-xs font-mono text-gray-400 w-14"><span className="inline-block h-2.5 w-2.5 rounded-full border border-black/40" style={{ backgroundColor: colorFor(d) }} />{denomLabel(d)}</span>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              disabled={!canOperate}
              value={raw(b.tableNumber, kind, d) ?? ''}
              onChange={(e) => setCount(b.tableNumber, kind, d, e.target.value)}
              onBlur={(e) => save(b.tableNumber, kind, d, e.target.value)}
              className="no-spin w-24 px-2 py-1.5 bg-gray-950 border border-gray-700 rounded text-right text-white font-mono text-sm focus:outline-none focus:border-amber-500 disabled:opacity-50"
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between border-t border-gray-800 pt-2 text-sm">
        <span className="text-gray-500">Total</span>
        <span className="font-mono text-gray-200">{fmtCents(boxTotal(b.tableNumber, kind, denoms))}</span>
      </div>
    </div>
  );

  return (
    <div>
      <CageBalancingTabs />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Boxes className="text-amber-400" /> Drop Count
          </h1>
          <p className="text-sm text-gray-400">
            Count each table's drop box and jackpot drop by chip denomination. Commit posts the totals
            to the shift (table revenue + Box Count component).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CageDatePicker />
          <select
            value={shift}
            onChange={(e) => setShift(e.target.value)}
            className="rounded border border-gray-700 bg-gray-900 px-2 py-1.5 text-sm text-white"
          >
            {[...new Set(['day', 'swing', ...shiftList.map((x) => x.shift)])].map((name) => (
              <option key={name} value={name}>{name.charAt(0).toUpperCase() + name.slice(1)}</option>
            ))}
          </select>
          {canOperate && (
            <button
              onClick={() => setEditColors((v) => !v)}
              className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
            >
              Colors
            </button>
          )}
          <button
            onClick={load}
            className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          {canOperate && (
            <button
              onClick={saveAll}
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-gray-700 px-3 py-2 text-sm font-medium text-white hover:bg-gray-600 disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save counts'}
            </button>
          )}
        </div>
      </div>
      {pin && <PinModal req={pin} onDone={() => setPin(null)} />}

      {locked && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-sky-700/50 bg-sky-900/20 p-3 text-sm text-sky-200">
          <Lock size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">The {shift} shift for {date} is closed — that is fine.</div>
            <div className="mt-0.5 text-sky-300/80">
              Enter the count and post it. The shift stays closed: posting writes the box count, recomputes
              that sheet in place, and carries the corrected ending into the next shift if it is still open.
              Over/short does not move — the box count cancels out of it.
              Do <span className="font-medium">not</span> reopen the sheet to do this.
            </div>
          </div>
        </div>
      )}

      {lastPost && (
        <div className="mb-4 rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-3 text-sm text-emerald-300">
          <div className="flex items-start gap-2">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <div className="w-full">
              <div className="font-medium">
                Posted {fmtCents(lastPost.boxCountCents)} to {lastPost.businessDate} {lastPost.shift} without reopening it.
              </div>
              {lastPost.endingBefore != null && lastPost.endingAfter != null && (
                <div className="mt-0.5 text-emerald-400/80">
                  Ending {fmtCents(lastPost.endingBefore)} &rarr; {fmtCents(lastPost.endingAfter)}. Over/short unchanged.
                </div>
              )}
              {(lastPost.propagated || []).length === 0 && (
                <div className="mt-1 text-emerald-400/80">Nothing follows this shift yet, so nothing to carry forward.</div>
              )}
              {(lastPost.propagated || []).map((st) => (
                <div key={st.shiftId} className="mt-1">
                  {st.deltaCents ? (
                    <span>
                      {st.label}: opening {fmtCents(st.beforeCents)} &rarr; {fmtCents(st.afterCents)}
                      {st.wasClosed ? ' — settled sheet corrected' : ''}
                      {st.wasClosed && st.overShortAfter != null
                        ? `, over/short now ${fmtCents(st.overShortAfter)}`
                        : ''}
                    </span>
                  ) : (
                    <span>{st.label}: opening unchanged</span>
                  )}
                  {(st.skippedOverrides || []).length > 0 && (
                    <span className="block text-amber-300/90">
                      Left alone: the hand-entered {st.skippedOverrides.join(' and ')} opening on {st.label}.
                      Somebody set that deliberately, so it was not overwritten — check it still belongs.
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {lastPost && (lastPost.warnings || []).length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-600/50 bg-amber-900/20 p-3 text-sm text-amber-300">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">This drop changed something that was already settled.</div>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {lastPost.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      )}

      {otherCommitted.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-600/50 bg-amber-900/20 p-3 text-sm text-amber-300">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              The drop for {date} is already committed to the{' '}
              {otherCommitted.map((x) => x.shift).join(' and ')} shift
              {otherCommitted.length > 1 ? 's' : ''}
              {' — '}
              {otherCommitted.map((x) => fmtCents(x.boxCountCents)).join(' + ')}.
            </div>
            <div className="mt-0.5 text-amber-200/70">
              The boxes are normally pulled once a day. Committing here as well adds a second
              copy: the day&rsquo;s table revenue and its jackpot meters both double, and the
              over/short still balances, so nothing else will flag it.
            </div>
          </div>
        </div>
      )}

      {thisCommittedCents > 0 && !locked && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800/60 p-3 text-sm text-gray-300">
          <CheckCircle2 size={16} className="shrink-0 text-emerald-400" />
          This {shift} shift already has a committed box count of{' '}
          <span className="font-mono">{fmtCents(thisCommittedCents)}</span>. Committing again
          replaces it rather than adding to it.
        </div>
      )}

      {editColors && (
        <div className="mb-4 rounded-lg border border-gray-700 bg-gray-900 p-4">
          <div className="mb-3 text-sm font-semibold text-white">Chip colors</div>
          <div className="flex flex-wrap gap-4">
            {[...new Set([...(layout ? layout.dropDenoms : []), ...(layout ? layout.jackpotDenoms : [])])].map((d) => (
              <label key={d} className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="color"
                  value={colorFor(d)}
                  onChange={(e) => setChipColors((p) => ({ ...p, [d]: e.target.value }))}
                  className="h-7 w-9 cursor-pointer rounded border border-gray-700 bg-transparent p-0"
                />
                {denomLabel(d)}
              </label>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={saveColors}
              disabled={savingColors}
              className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-40"
            >
              {savingColors ? 'Saving…' : 'Save colors'}
            </button>
            <button
              onClick={() => { setEditColors(false); load(); }}
              className="rounded-lg border border-gray-600 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {loading || !layout ? (
        <div className="p-8 text-center text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-8">
          {/* Drop boxes */}
          <section>
            <h2 className="mb-3 text-xs uppercase tracking-wide text-gray-400">Drop boxes</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {layout.dropBoxes.map((b) => renderBox(b, 'drop', layout.dropDenoms))}
            </div>
          </section>

          {/* Jackpot boxes */}
          <section>
            <h2 className="mb-3 text-xs uppercase tracking-wide text-gray-400">Jackpot drop</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {layout.jackpotBoxes.map((b) => renderBox(b, 'jackpot', layout.jackpotDenoms))}
            </div>
          </section>

          {/* Totals */}
          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-700 bg-gray-900 p-4">
              <h3 className="mb-2 text-sm font-semibold text-white">Drop totals</h3>
              {Object.entries(dropByGame).map(([g, v]) => (
                <div key={g} className="flex justify-between py-0.5 text-sm">
                  <span className="text-gray-400">{g}</span>
                  <span className="font-mono text-gray-200">{fmtCents(v)}</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between border-t border-gray-700 pt-2 font-semibold">
                <span className="text-gray-300">Grand total</span>
                <span className="font-mono text-white">{fmtCents(grandDrop)}</span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-700 bg-gray-900 p-4">
              <h3 className="mb-2 text-sm font-semibold text-white">Jackpot totals</h3>
              {Object.entries(jackpotByGame).map(([g, v]) => (
                <div key={g} className="flex justify-between py-0.5 text-sm">
                  <span className="text-gray-400">{g}</span>
                  <span className="font-mono text-gray-200">{fmtCents(v)}</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between border-t border-gray-700 pt-2 font-semibold">
                <span className="text-gray-300">Grand total</span>
                <span className="font-mono text-white">{fmtCents(grandJackpot)}</span>
              </div>
            </div>
          </section>

          {canOperate && (
            <div className="flex items-center justify-end gap-3">
              <span className="text-xs text-gray-500">
                {locked
                  ? `Posts per-table drop + jackpot to the closed ${shift} shift, sets its Box Count to ${fmtCents(grandDrop + grandJackpot)}, and re-carries the next shift.`
                  : `Commits per-table drop + jackpot to the ${shift} shift and sets its Box Count to ${fmtCents(grandDrop + grandJackpot)} (drop + jackpot).`}
              </span>
              <button
                onClick={commit}
                disabled={committing}
                className="flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-black disabled:opacity-40"
              >
                <CheckCircle2 size={16} /> {committing ? (locked ? 'Posting…' : 'Committing…') : (locked ? 'Post the drop' : 'Commit to shift')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
